import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Search, MapPin, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { useDeliveryLocation } from "../contexts/DeliveryLocationContext";
import { api, assetUrl, formatINR } from "../lib/api";
import { ProductImage } from "./ProductImage";
import { PushBell } from "./PushBell";

const WHATSAPP_SUPPORT =
  "https://wa.me/917980016662?text=" +
  encodeURIComponent("Hello Quick Door Now Support, I need help with my order.");

const ROTATING = [
  "Search food...",
  "Search grocery...",
  "Search vegetables...",
  "Search fruits...",
  "Search daily essentials...",
];

export function QuickHeader({ businessInfo }) {
  const navigate = useNavigate();
  const { location } = useDeliveryLocation();
  const [query, setQuery] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const recognitionRef = useRef(null);
  const boxRef = useRef(null);
  const appDownload = businessInfo.app_download || {};
  const appHref =
    appDownload.active_source === "apk" ? appDownload.apk_url : appDownload.play_store_url;

  useEffect(() => {
    const timer = setInterval(() => setPlaceholderIndex((i) => (i + 1) % ROTATING.length), 2500);
    return () => clearInterval(timer);
  }, []);

  // Live product suggestions while typing (partial match, debounced).
  useEffect(() => {
    const term = query.trim();
    if (term.length < 1) {
      setSuggestions([]);
      return undefined;
    }
    const handle = setTimeout(() => {
      api
        .get("/products", { params: { q: term } })
        .then((response) => setSuggestions((response.data || []).slice(0, 8)))
        .catch(() => setSuggestions([]));
    }, 160);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  const runSearch = (value) => {
    if (value.trim()) {
      setOpen(false);
      navigate(`/c/all?q=${encodeURIComponent(value.trim())}`);
    }
  };

  const search = (event) => {
    event.preventDefault();
    runSearch(query);
  };

  const pickSuggestion = (product) => {
    setOpen(false);
    setQuery("");
    navigate(`/products/${product.id}`);
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice search is not supported on this browser");
      return;
    }
    if (!recognitionRef.current) {
      recognitionRef.current = new SR();
      recognitionRef.current.lang = "en-IN";
      recognitionRef.current.interimResults = false;
      recognitionRef.current.onresult = (e) => {
        const text = e.results?.[0]?.[0]?.transcript || "";
        setQuery(text);
        runSearch(text);
      };
      recognitionRef.current.onerror = () => toast.error("Could not hear you. Please try again.");
    }
    try {
      recognitionRef.current.start();
      toast.info("Listening…");
    } catch {
      /* already started */
    }
  };

  const showDropdown = open && query.trim().length >= 1;

  return (
    <header className="sticky top-0 z-40 border-b border-orange-100 bg-white/95 backdrop-blur-md">
      <div className="mx-auto max-w-xl px-3 pb-3 pt-2.5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="brand-logo-button"
            className="flex shrink-0 items-center"
            aria-label="Quick Door Now home"
          >
            <img
              src="/qdn-logo.png"
              alt="Quick Door Now"
              className="h-11 w-11 rounded-xl object-cover shadow-sm"
              data-testid="brand-logo-image"
            />
          </button>

          <div ref={boxRef} className="relative ml-1 min-w-0 flex-1">
            <form
              onSubmit={search}
              className="flex min-w-0 items-center gap-2 rounded-full bg-slate-100 px-3 py-2.5"
            >
              <Search size={17} className="shrink-0 text-slate-400" />
              <input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                data-testid="quick-search-input"
                placeholder={ROTATING[placeholderIndex]}
                autoComplete="off"
                className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none ring-0 placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-0"
              />
              <button
                type="button"
                onClick={startVoice}
                data-testid="voice-search-button"
                aria-label="Voice search"
                className="shrink-0 text-slate-400 transition-colors hover:text-[#FF5200]"
              >
                <Mic size={17} />
              </button>
            </form>

            {showDropdown && (
              <div
                data-testid="search-suggestions"
                className="absolute inset-x-0 top-full z-50 mt-2 max-h-[60vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white py-1 shadow-xl"
              >
                {suggestions.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-slate-500" data-testid="search-no-results">
                    No products match "{query.trim()}"
                  </p>
                ) : (
                  suggestions.map((product) => {
                    const price =
                      product.price ?? product.variants?.find((v) => v.active !== false)?.price;
                    return (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => pickSuggestion(product)}
                        data-testid={`search-suggestion-${product.id}`}
                        className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-slate-50 active:bg-slate-100"
                      >
                        <ProductImage
                          src={assetUrl(product.image)}
                          alt={product.name}
                          className="h-10 w-10 shrink-0 rounded-lg border border-slate-100 object-cover"
                        />
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
                          {product.name}
                        </span>
                        {price != null && (
                          <span className="shrink-0 text-sm font-bold text-slate-700">
                            {formatINR(price)}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {appHref ? (
            <a
              href={appHref}
              target="_blank"
              rel="noreferrer"
              data-testid="download-app-button"
              className="shrink-0 rounded-full bg-slate-900 px-3 py-2 text-[11px] font-extrabold text-white transition-colors hover:bg-slate-800"
            >
              App ↓
            </a>
          ) : (
            <button
              type="button"
              disabled
              data-testid="download-app-button"
              title="Set the active app link in Admin Storefront"
              className="shrink-0 rounded-full bg-slate-200 px-3 py-2 text-[11px] font-extrabold text-slate-500"
            >
              App ↓
            </button>
          )}

          <PushBell />

          <a
            href={WHATSAPP_SUPPORT}
            target="_blank"
            rel="noreferrer"
            data-testid="whatsapp-support-link"
            aria-label="WhatsApp support"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#25D366] shadow-sm transition-transform hover:brightness-105 active:scale-95"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </a>
        </div>

        <button
          type="button"
          onClick={() => navigate("/select-location")}
          data-testid="quick-location-selector"
          className="mt-2.5 flex w-full items-center gap-1.5 text-left"
        >
          <MapPin size={14} className="shrink-0 text-[#FF5200]" />
          <span className="min-w-0 flex-1 truncate text-xs font-bold text-slate-900">
            <span className="font-semibold text-slate-500">Delivering to: </span>
            {location?.address || "Select your location"}
          </span>
          <span
            data-testid="delivery-eta-tag"
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-extrabold text-amber-900"
          >
            <Clock3 size={11} /> 10–15 mins
          </span>
        </button>
      </div>
    </header>
  );
}
